import { Button } from './ui';

interface CategorizedError {
  title: string;
  message: string;
}

/**
 * Categorize a raw error string into a user-friendly title + message.
 * Based on the Download page's pattern, extended for upload/network errors.
 */
export function categorizeError(error: string, context?: { reference?: string }): CategorizedError {
  const lower = error.toLowerCase();

  // Reference format validation
  if (context?.reference && !/^[a-f0-9]{64}$/i.test(context.reference)) {
    return {
      title: 'Invalid Link',
      message: 'This link appears to be malformed. Please check the URL and try again.',
    };
  }

  // Decryption errors
  if (lower.includes('decrypt')) {
    return {
      title: 'Decryption Failed',
      message: 'Unable to decrypt this file. It may have been encrypted for a different account.',
    };
  }

  // Timeout
  if (lower.includes('timeout')) {
    return {
      title: 'Connection Timeout',
      message: 'The server took too long to respond. Please try again in a few moments.',
    };
  }

  // Network errors
  if (lower.includes('network') || lower.includes('fetch') || lower.includes('connection') || lower.includes('failed to fetch')) {
    return {
      title: 'Connection Error',
      message: 'Unable to connect to the network. Please check your connection and try again.',
    };
  }

  // Not found
  if (lower.includes('not found') || lower.includes('404')) {
    return {
      title: 'File Not Found',
      message: 'The file could not be found. It may have expired or been removed.',
    };
  }

  // Size limit
  if (lower.includes('size') || lower.includes('too large') || lower.includes('limit')) {
    return {
      title: 'File Too Large',
      message: error,
    };
  }

  // ENS / recipient errors
  if (lower.includes('ens') || lower.includes('recipient') || lower.includes('resolve')) {
    return {
      title: 'Recipient Error',
      message: 'Could not resolve the recipient. Please check the username and try again.',
    };
  }

  // Generic fallback
  return {
    title: 'Something Went Wrong',
    message: error || 'An unexpected error occurred. Please try again.',
  };
}

interface ErrorDisplayProps {
  error: string;
  context?: { reference?: string };
  onRetry?: () => void;
  onDismiss?: () => void;
  onHome?: () => void;
}

export function ErrorDisplay({ error, context, onRetry, onDismiss, onHome }: ErrorDisplayProps) {
  const { title, message } = categorizeError(error, context);

  return (
    <div className="error-display">
      <h2 className="error-display-title">{title}</h2>
      <p className="error-display-message">{message}</p>
      <div className="error-display-actions">
        {onRetry && (
          <Button variant="outline" onClick={onRetry}>
            Try Again
          </Button>
        )}
        {onDismiss && (
          <button className="link-button" onClick={onDismiss}>
            Dismiss
          </button>
        )}
        {onHome && (
          <button className="link-button" onClick={onHome}>
            Go Home
          </button>
        )}
      </div>
    </div>
  );
}
